import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Editor } from "@tiptap/react";
import { useApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";

/**
 * Insertions registry — what LaTeX commands / environments are
 * available in this document, given its installed packages.
 *
 * Wraps GET /api/lilia/insertions?docId=... and shares the cache
 * between the InsertionsPanel side panel and the ⌘K command palette,
 * so users see the same list in both places without doubling the
 * network request.
 */
export interface InsertionDto {
  name: string;
  kind: "command" | "environment" | "declaration" | "length" | "counter";
  packageSlug: string | null;
  semanticCategory: string | null;
  coverageLevel: "full" | "shimmed" | "partial" | "none" | "unsupported";
  expectsBody: boolean;
  notes: string | null;
  /**
   * Optional snippet from the catalog. The literal `|CURSOR|` marks
   * caret position. When set, this template wins over the default
   * shapes in `insertTokenIntoEditor`. Edited via Lilia Admin → DB,
   * data-driven; no editor redeploy required to change a template.
   */
  insertTemplate: string | null;
}

const CURSOR_MARKER = "|CURSOR|";
const SELECTION_MARKER = "|SELECTION|";

/**
 * Insertion source — what surface the user picked the token from.
 * Threaded through `insertTokenIntoEditor` so the server-side
 * telemetry can break down usage by surface.
 */
export type InsertionSource = "panel" | "palette" | "slash" | "package-modal";

/**
 * Telemetry queue — module-level so any caller of `insertTokenIntoEditor`
 * can record an event without prop-drilling a fetcher through the React
 * tree. The provider component (typically EditorPage) registers the
 * authed fetcher once via `setInsertionTelemetryFetcher`; the queue
 * flushes to /api/lilia/insertions/events every 3s or when 10 events
 * are buffered, whichever comes first. Best-effort; failures are
 * silent because telemetry is not on the critical path.
 */
type AuthedFetcher = (url: string, init?: RequestInit) => Promise<Response>;

interface QueuedInsertionEvent {
  name: string;
  kind: string;
  packageSlug: string | null;
  source: InsertionSource;
  documentId: string | null;
  wrappedSelection: boolean;
}

const TELEMETRY_FLUSH_MS = 3000;
const TELEMETRY_FLUSH_THRESHOLD = 10;

let telemetryFetcher: AuthedFetcher | null = null;
let telemetryApiBase = "";
let telemetryQueue: QueuedInsertionEvent[] = [];
let telemetryFlushTimer: ReturnType<typeof setTimeout> | null = null;

export function setInsertionTelemetryFetcher(fn: AuthedFetcher | null, apiBase: string) {
  telemetryFetcher = fn;
  telemetryApiBase = apiBase;
}

async function flushTelemetryQueue() {
  telemetryFlushTimer = null;
  if (telemetryQueue.length === 0 || !telemetryFetcher) return;
  const batch = telemetryQueue.splice(0);
  try {
    await telemetryFetcher(`${telemetryApiBase}/lilia/insertions/events`, {
      method: "POST",
      body: JSON.stringify({
        events: batch.map((e) => ({
          name: e.name,
          kind: e.kind,
          packageSlug: e.packageSlug,
          source: e.source,
          documentId: e.documentId,
          wrappedSelection: e.wrappedSelection,
        })),
      }),
    });
  } catch {
    // Best-effort. Drop the batch — re-queueing risks unbounded growth
    // if the network is down for a long stretch and the user keeps
    // inserting. Sentry catches systemic failures via the API side.
  }
}

function scheduleTelemetryFlush() {
  if (telemetryQueue.length >= TELEMETRY_FLUSH_THRESHOLD) {
    if (telemetryFlushTimer) {
      clearTimeout(telemetryFlushTimer);
      telemetryFlushTimer = null;
    }
    flushTelemetryQueue();
    return;
  }
  if (telemetryFlushTimer) return;
  telemetryFlushTimer = setTimeout(flushTelemetryQueue, TELEMETRY_FLUSH_MS);
}

// Flush on page unload so the last batch isn't lost. sendBeacon would
// be more reliable but our endpoint is [Authorize] and beacon doesn't
// carry our auth header — fetch with keepalive is the next-best.
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    if (telemetryQueue.length === 0 || !telemetryFetcher) return;
    const batch = telemetryQueue.splice(0);
    // Fire-and-forget; keepalive lets the request survive the unload.
    try {
      telemetryFetcher(`${telemetryApiBase}/lilia/insertions/events`, {
        method: "POST",
        body: JSON.stringify({ events: batch }),
        keepalive: true,
      });
    } catch { /* unload context — nothing to do */ }
  });
}

/**
 * Recently-used insert tracking — bookkeeping in localStorage so the
 * panel and ⌘K palette can pin frequently-used tokens at the top.
 * Stored as a list of { key, lastUsedAt } per user-agent. Capped at
 * 12 items; LRU pruning keeps the working set small.
 */
const RECENT_KEY = "lilia.editor.recent-insertions";
const RECENT_CAP = 12;

interface RecentEntry {
  /** "{packageSlug ?? kernel}/{kind}/{name}" — same id we use in the palette. */
  key: string;
  lastUsedAt: number;
  hits: number;
}

export function tokenKey(t: Pick<InsertionDto, "name" | "kind" | "packageSlug">): string {
  return `${t.packageSlug ?? "kernel"}/${t.kind}/${t.name}`;
}

function readRecents(): RecentEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (e) => e && typeof e.key === "string" && typeof e.lastUsedAt === "number"
      )
      .map((e) => ({
        key: e.key as string,
        lastUsedAt: e.lastUsedAt as number,
        hits: typeof e.hits === "number" ? e.hits : 1,
      }));
  } catch {
    return [];
  }
}

function writeRecents(entries: RecentEntry[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(entries.slice(0, RECENT_CAP)));
  } catch {
    // Quota / disabled storage — silent; recents are best-effort.
  }
}

/**
 * Record a successful insert — dual purpose:
 *   1. Update the per-user recents (localStorage, drives the panel +
 *      ⌘K "recent" sections)
 *   2. Queue a server-side telemetry event for the catalog backfill
 *      priority report (latex_insertion_events)
 *
 * Telemetry is best-effort and silent on failure. The recents path
 * always runs even if the network is down.
 */
export function recordInsertion(
  token: InsertionDto,
  source: InsertionSource = "panel",
  documentId: string | null = null,
  wrappedSelection: boolean = false
): void {
  // ── recents (localStorage) ────────────────────────────────────────
  const key = tokenKey(token);
  const now = Date.now();
  const current = readRecents();
  const existing = current.find((e) => e.key === key);
  let next: RecentEntry[];
  if (existing) {
    next = current.map((e) =>
      e.key === key ? { ...e, lastUsedAt: now, hits: e.hits + 1 } : e
    );
  } else {
    next = [{ key, lastUsedAt: now, hits: 1 }, ...current];
  }
  next.sort((a, b) => b.lastUsedAt - a.lastUsedAt || b.hits - a.hits);
  writeRecents(next);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("lilia:recents-changed"));
  }

  // ── telemetry queue (server) ──────────────────────────────────────
  telemetryQueue.push({
    name: token.name,
    kind: token.kind,
    packageSlug: token.packageSlug,
    source,
    documentId,
    wrappedSelection,
  });
  scheduleTelemetryFlush();
}

export function useInsertions(documentId: string | null | undefined) {
  const { fetchWithAuth } = useApiContext();

  return useQuery({
    queryKey: ["insertions", documentId],
    queryFn: async () => {
      const res = await fetchWithAuth(`${API_BASE}/lilia/insertions?docId=${documentId}`);
      return handleResponse<InsertionDto[]>(res);
    },
    staleTime: 60_000,
    enabled: !!documentId,
  });
}

/**
 * Insert a LaTeX token at the active editor's cursor. Shared between
 * the InsertionsPanel side panel and the ⌘K command palette so both
 * surfaces produce identical output. Returns false when there is no
 * focused editor (caller can show a hint).
 *
 * Phase 1 templates:
 *   - command (`\name{}`)     — inserts then moves the cursor 1 char back
 *                               so the user types between the braces
 *   - environment             — inserts as a new `\begin{}…\end{}` block
 *
 * Phase 2 plumbs `insert_template` from the catalog so per-token
 * starter snippets (e.g. an algorithm skeleton) replace these defaults.
 */
/**
 * React hook over the localStorage-backed recents list. Returns the
 * top N most-recent entries, refreshed on the `lilia:recents-changed`
 * event the recorder dispatches and on window focus (so multiple
 * tabs stay in sync without a real broadcast channel).
 */
export function useRecentInsertions(limit = 6): RecentEntry[] {
  const [entries, setEntries] = useState<RecentEntry[]>(() => readRecents());

  useEffect(() => {
    const refresh = () => setEntries(readRecents());
    if (typeof window === "undefined") return;
    window.addEventListener("lilia:recents-changed", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("lilia:recents-changed", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  return useMemo(() => entries.slice(0, limit), [entries, limit]);
}

/**
 * Resolve a list of recent keys against the current document's
 * insertion catalog. Recents that no longer match an installable
 * token (package uninstalled, catalog change) are silently dropped
 * from the result.
 */
export function useRecentInsertionTokens(
  insertions: InsertionDto[],
  limit = 6
): InsertionDto[] {
  const recents = useRecentInsertions(limit);
  return useMemo(() => {
    const byKey = new Map(insertions.map((i) => [tokenKey(i), i]));
    const out: InsertionDto[] = [];
    for (const r of recents) {
      const match = byKey.get(r.key);
      if (match) out.push(match);
    }
    return out;
  }, [insertions, recents]);
}

export function insertTokenIntoEditor(
  token: InsertionDto,
  editor: Editor | null,
  options?: { source?: InsertionSource; documentId?: string | null }
): boolean {
  if (!editor) return false;

  // Pull the current selection text first; the helper uses it to
  // resolve |SELECTION| markers and to decide whether to substitute
  // selected text into the |CURSOR| slot.
  const { from, to, empty } = editor.state.selection;
  const selectedText = empty ? "" : editor.state.doc.textBetween(from, to, " ");

  // Catalog-driven template wins. Look for |CURSOR| (caret slot) and
  // |SELECTION| (where the user's selected text should land).
  let ok: boolean;
  if (token.insertTemplate) {
    ok = insertWithMarkers(editor, token.insertTemplate, selectedText);
  } else if (token.kind === "environment" || token.expectsBody) {
    // Fallback default templates — used when the catalog row has no
    // explicit insert_template. Both shapes carry |SELECTION| so the
    // selection-wrap behavior is uniform.
    const snippet = `\\begin{${token.name}}\n  ${SELECTION_MARKER}\n\\end{${token.name}}`;
    ok = insertWithMarkers(editor, `\n${snippet}\n`, selectedText);
  } else {
    const snippet = `\\${token.name}{${SELECTION_MARKER}}`;
    ok = insertWithMarkers(editor, snippet, selectedText);
  }

  if (ok) {
    recordInsertion(
      token,
      options?.source ?? "panel",
      options?.documentId ?? null,
      Boolean(selectedText),
    );
  }
  return ok;
}

/**
 * Insert a snippet that may contain `|CURSOR|` and/or `|SELECTION|`
 * markers. Both are stripped before the snippet reaches TipTap.
 *
 * Resolution rules:
 *   - `|SELECTION|` is replaced by the user's selected text (or empty
 *     when there's no selection). When selection is non-empty, the
 *     existing selection is deleted first so the snippet replaces it.
 *   - `|CURSOR|` marks where the caret lands after insertion. If the
 *     snippet has no `|SELECTION|` *and* the user has a selection, the
 *     selection takes the `|CURSOR|` slot — which makes naive templates
 *     like `\emph{|CURSOR|}` behave intuitively as wrappers without
 *     needing every template to be touched.
 *
 * Single source of truth for cursor + selection handling so panel +
 * palette emit identical behavior.
 */
function insertWithMarkers(editor: Editor, snippet: string, selectedText: string): boolean {
  let processed = snippet;
  const hasSelectionMarker = processed.includes(SELECTION_MARKER);
  const hasCursorMarker = processed.includes(CURSOR_MARKER);

  if (hasSelectionMarker) {
    processed = processed.replace(SELECTION_MARKER, selectedText);
  } else if (selectedText && hasCursorMarker) {
    // No explicit |SELECTION|, but the user has selected text — let
    // it land in the |CURSOR| slot so simple wrappers work without
    // every template being authored with a selection slot.
    processed = processed.replace(CURSOR_MARKER, selectedText);
  }

  // Recompute cursor index *after* selection substitution because the
  // substituted text shifts the |CURSOR| position by its length.
  const cursorIdx = processed.indexOf(CURSOR_MARKER);
  const cleanSnippet = cursorIdx >= 0
    ? processed.replace(CURSOR_MARKER, "")
    : processed;

  // Replace existing selection (if any) with the snippet. TipTap's
  // chain handles the delete + insert atomically so the operation is
  // a single undo step.
  const chain = editor.chain().focus();
  if (selectedText) {
    chain.deleteSelection();
  }
  chain.insertContent(cleanSnippet).run();

  // Caret placement — only when an explicit |CURSOR| marker survived
  // selection substitution. Otherwise TipTap's default end-of-insertion
  // caret position applies (which is fine for selection-only flows).
  if (cursorIdx >= 0) {
    const docSize = editor.state.doc.content.size;
    // After insert, selection.from sits right after the inserted text.
    // Walk back by (cleanSnippet.length - cursorIdx) to land on the
    // marker's original position.
    const targetPos = editor.state.selection.from - (cleanSnippet.length - cursorIdx);
    if (targetPos >= 0 && targetPos <= docSize) {
      editor.chain().focus().setTextSelection(targetPos).run();
    }
  }
  return true;
}

/**
 * UI helper — tells the InsertionsPanel whether a token will wrap the
 * current selection on click. Used to render a small "wraps selection"
 * hint when the user has text highlighted.
 */
export function tokenWrapsSelection(token: InsertionDto): boolean {
  if (token.insertTemplate) {
    return token.insertTemplate.includes(SELECTION_MARKER) ||
           token.insertTemplate.includes(CURSOR_MARKER);
  }
  // All default templates carry |SELECTION| now.
  return true;
}
